"use client";

import React from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import Image from "next/image";
import { HeroHeader } from "@/components/hero8-header";

export default function HeroSection() {
  return (
    <>
      <HeroHeader />
      <main className="overflow-x-hidden">
        <section>
          <div className="pb-12 pt-6 md:pb-20 md:pt-12 lg:pb-32 lg:pt-18 mt-10 lg:mt-0">
            <div className="relative mx-auto flex max-w-6xl flex-col px-6 lg:block">
              <div className="flex flex-col-reverse items-center lg:flex-row lg:items-start lg:justify-between">
                <div className="mx-auto max-w-lg text-center lg:ml-0 lg:w-1/2 lg:text-left">
                  <h1 className="mt-8 max-w-2xl text-balance text-5xl font-bold md:text-6xl lg:mt-16 xl:text-7xl animate-hero-fadein">
                    WeeKrt - Your Local Delivery Companion
                  </h1>
                  <p className="mt-8 max-w-2xl text-pretty text-lg animate-hero-fadein delay-100">
                    Get products from your favorite nearby stores delivered to
                    your doorstep, or pick them up yourself. Find nearby shops,
                    Earn WeeCoins on every purchase!
                  </p>
                  <div className="mt-8 flex flex-wrap justify-center gap-2 lg:justify-start animate-hero-fadein delay-200">
                    <Button
                      asChild
                      size="lg"
                      className="py-4 px-8 text-lg bg-green-600 hover:bg-green-700"
                    >
                      <Link
                        href="https://play.google.com/store"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <span className="flex items-center gap-2">
                          <svg
                            className="w-5 h-5"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                          >
                            <path d="M1.5 3.5v17l12-8.5-12-8.5zm13.5 8.5l7.5 5.25v-10.5l-7.5 5.25zm0 0l-2.25 1.5 2.25 1.5v-3zm0 0v-3l2.25 1.5-2.25 1.5z" />
                          </svg>{" "}
                          Download on Play Store
                        </span>
                      </Link>
                    </Button>
                  </div>
                  <div className="mt-8 flex flex-wrap gap-x-2 gap-y-2 max-w-2xl justify-center lg:justify-start mx-auto animate-hero-fadein delay-300">
                    <span className="bg-green-100 text-green-800 rounded-full px-4 py-2 text-base font-semibold">
                      Support Local
                    </span>
                    <span className="bg-yellow-100 text-yellow-800 rounded-full px-4 py-2 text-base font-semibold">
                      Eat Fresh, Not Frozen
                    </span>
                    <span className="bg-purple-100 text-purple-800 rounded-full px-4 py-2 text-base font-semibold">
                      Shop Smart, Shop Nearby
                    </span>
                    <span className="bg-blue-100 text-blue-800 rounded-full px-4 py-2 text-base font-semibold">
                      Convenience Meets Community
                    </span>
                    <span className="bg-pink-100 text-pink-800 rounded-full px-4 py-2 text-base font-semibold">
                      Your Local Favorites, Delivered
                    </span>
                  </div>
                </div>
                <div className="flex justify-center lg:justify-end w-full lg:w-1/2 mb-8 lg:mb-0 lg:mt-24 animate-hero-img-slidein delay-400 relative">
                  {/* Realistic bike exhaust smoke effect, behind the image */}
                  <div className="absolute bottom-14 left-2 z-0 w-56 h-40 pointer-events-none animate-bike-smoke">
                    <svg
                      width="224"
                      height="160"
                      viewBox="0 0 224 160"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <ellipse
                        cx="60"
                        cy="120"
                        rx="38"
                        ry="16"
                        fill="url(#smoke1)"
                        fillOpacity="0.32"
                        filter="url(#blur1)"
                      />
                      <ellipse
                        cx="120"
                        cy="90"
                        rx="32"
                        ry="14"
                        fill="url(#smoke2)"
                        fillOpacity="0.18"
                        filter="url(#blur2)"
                      />
                      <ellipse
                        cx="170"
                        cy="130"
                        rx="22"
                        ry="10"
                        fill="url(#smoke3)"
                        fillOpacity="0.13"
                        filter="url(#blur3)"
                      />
                      <ellipse
                        cx="100"
                        cy="110"
                        rx="18"
                        ry="8"
                        fill="url(#smoke4)"
                        fillOpacity="0.15"
                        filter="url(#blur4)"
                      />
                      <defs>
                        <radialGradient
                          id="smoke1"
                          cx="0.5"
                          cy="0.5"
                          r="0.5"
                          fx="0.5"
                          fy="0.5"
                        >
                          <stop stopColor="#fff" />
                          <stop offset="1" stopColor="#bbb" />
                        </radialGradient>
                        <radialGradient
                          id="smoke2"
                          cx="0.5"
                          cy="0.5"
                          r="0.5"
                          fx="0.5"
                          fy="0.5"
                        >
                          <stop stopColor="#eee" />
                          <stop offset="1" stopColor="#888" />
                        </radialGradient>
                        <radialGradient
                          id="smoke3"
                          cx="0.5"
                          cy="0.5"
                          r="0.5"
                          fx="0.5"
                          fy="0.5"
                        >
                          <stop stopColor="#fff" />
                          <stop offset="1" stopColor="#444" />
                        </radialGradient>
                        <radialGradient
                          id="smoke4"
                          cx="0.5"
                          cy="0.5"
                          r="0.5"
                          fx="0.5"
                          fy="0.5"
                        >
                          <stop stopColor="#fff" />
                          <stop offset="1" stopColor="#bbb" />
                        </radialGradient>
                        <filter
                          id="blur1"
                          x="-20"
                          y="100"
                          width="120"
                          height="40"
                        >
                          <feGaussianBlur stdDeviation="8" />
                        </filter>
                        <filter id="blur2" x="80" y="76" width="80" height="28">
                          <feGaussianBlur stdDeviation="10" />
                        </filter>
                        <filter
                          id="blur3"
                          x="140"
                          y="120"
                          width="60"
                          height="20"
                        >
                          <feGaussianBlur stdDeviation="12" />
                        </filter>
                        <filter
                          id="blur4"
                          x="80"
                          y="102"
                          width="40"
                          height="16"
                        >
                          <feGaussianBlur stdDeviation="7" />
                        </filter>
                      </defs>
                    </svg>
                  </div>
                  <Image
                    className=" object-contain relative z-20"
                    src="/hero-image3.png"
                    alt="WeeKrt food and groceries delivery illustration"
                    height={420}
                    width={520}
                    priority
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <style jsx global>{`
        @keyframes hero-fadein {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: none;
          }
        }
        .animate-hero-fadein {
          animation: hero-fadein 1.1s cubic-bezier(0.4, 0, 0.2, 1) both;
        }
        .animate-hero-fadein.delay-100 {
          animation-delay: 0.1s;
        }
        .animate-hero-fadein.delay-200 {
          animation-delay: 0.2s;
        }
        .animate-hero-fadein.delay-300 {
          animation-delay: 0.3s;
        }
        .animate-hero-fadein.delay-400 {
          animation-delay: 0.4s;
        }
        @keyframes hero-img-slidein {
          from {
            opacity: 0;
            transform: translateX(-100vw);
          }
          to {
            opacity: 1;
            transform: none;
          }
        }
        .animate-hero-img-slidein {
          animation: hero-img-slidein 3s cubic-bezier(0.4, 0, 0.2, 1) both;
        }
        .animate-hero-img-slidein.delay-400 {
          animation-delay: 0.4s;
        }
        @keyframes bike-smoke {
          0% {
            opacity: 0;
            transform: scale(0.7) translateX(0) translateY(0);
          }
          30% {
            opacity: 0.8;
          }
          60% {
            opacity: 0.7;
            transform: scale(1.05) translateX(30px) translateY(-10px);
          }
          100% {
            opacity: 0;
            transform: scale(1.2) translateX(90px) translateY(-30px);
          }
        }
        .animate-bike-smoke {
          animation: bike-smoke 3s cubic-bezier(0.4, 0, 0.2, 1) both;
          animation-delay: 0.4s;
        }
      `}</style>
    </>
  );
}
